/**
 * RateCardPage - Rate Card Management for Consulting Mode
 * Manage role-based rates, currency, and markup for proposals
 * 
 * Feature flag: features.consulting.ratecard
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DollarSign, Plus, Trash2, Edit2, Check, X, Save, 
  AlertTriangle, ArrowLeft, RefreshCw, Loader2, Settings
} from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useProductMode } from '../context/ProductModeContext';
import TierGate from '../components/TierGate';
import { toast } from 'sonner';

// Available roles
const ROLE_OPTIONS = [
  { key: 'PM', label: 'Project Manager' },
  { key: 'BA', label: 'Business Analyst' },
  { key: 'PO', label: 'Product Owner' },
  { key: 'UX', label: 'UX Designer' },
  { key: 'Dev', label: 'Developer' },
  { key: 'QA', label: 'QA Engineer' },
  { key: 'Eng', label: 'DevOps/Infrastructure' },
  { key: 'Other', label: 'Other' },
];

// Currency options
const CURRENCY_OPTIONS = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'ZAR', symbol: 'R', name: 'South African Rand' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
];

/**
 * Rate Card Row Component
 */
const RateCardRow = ({ role, onUpdate, onDelete, isEditing, setEditing, currency }) => {
  const [editData, setEditData] = useState(role);
  const currencySymbol = CURRENCY_OPTIONS.find(c => c.code === (role.currency || currency))?.symbol || '$';
  
  useEffect(() => {
    setEditData(role);
  }, [role]);
  
  const handleSave = () => {
    // Validate
    if (editData.baseRate < 0) {
      toast.error('Base rate must be >= 0');
      return;
    }
    if (editData.minRate !== null && editData.maxRate !== null && editData.minRate > editData.maxRate) {
      toast.error('Min rate cannot exceed max rate');
      return;
    }
    if (editData.markupPct < 0 || editData.markupPct > 100) {
      toast.error('Markup must be 0-100%');
      return;
    }
    
    onUpdate(editData);
    setEditing(null);
  };
  
  const handleCancel = () => {
    setEditData(role);
    setEditing(null);
  };
  
  if (isEditing) {
    return (
      <tr className="bg-purple-50">
        <td className="px-4 py-3">
          <select
            value={editData.role}
            onChange={(e) => setEditData({ ...editData, role: e.target.value })}
            className="w-full px-2 py-1.5 text-sm border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500"
          >
            {ROLE_OPTIONS.map(opt => (
              <option key={opt.key} value={opt.key}>{opt.label}</option>
            ))}
          </select>
        </td>
        <td className="px-4 py-3">
          <select
            value={editData.currency || currency}
            onChange={(e) => setEditData({ ...editData, currency: e.target.value })}
            className="w-full px-2 py-1.5 text-sm border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500"
          >
            {CURRENCY_OPTIONS.map(opt => (
              <option key={opt.code} value={opt.code}>{opt.code}</option>
            ))}
          </select>
        </td>
        <td className="px-4 py-3">
          <input
            type="number"
            value={editData.baseRate}
            onChange={(e) => setEditData({ ...editData, baseRate: parseFloat(e.target.value) || 0 })}
            className="w-24 px-2 py-1.5 text-sm text-right border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            min="0"
            step="1"
          />
        </td>
        <td className="px-4 py-3">
          <input
            type="number"
            value={editData.minRate || ''}
            onChange={(e) => setEditData({ ...editData, minRate: e.target.value ? parseFloat(e.target.value) : null })}
            placeholder="—"
            className="w-20 px-2 py-1.5 text-sm text-right border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            min="0"
          />
        </td>
        <td className="px-4 py-3">
          <input
            type="number"
            value={editData.maxRate || ''}
            onChange={(e) => setEditData({ ...editData, maxRate: e.target.value ? parseFloat(e.target.value) : null })}
            placeholder="—"
            className="w-20 px-2 py-1.5 text-sm text-right border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            min="0"
          />
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-1">
            <input
              type="number"
              value={editData.markupPct || 0}
              onChange={(e) => setEditData({ ...editData, markupPct: parseFloat(e.target.value) || 0 })}
              className="w-16 px-2 py-1.5 text-sm text-right border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              min="0"
              max="100"
            />
            <span className="text-sm text-gray-500">%</span>
          </div>
        </td>
        <td className="px-4 py-3 text-center">
          <input
            type="checkbox"
            checked={editData.active}
            onChange={(e) => setEditData({ ...editData, active: e.target.checked })}
            className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
          />
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-1 justify-end">
            <button
              onClick={handleSave}
              className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg"
              title="Save"
            >
              <Check className="w-4 h-4" />
            </button>
            <button
              onClick={handleCancel}
              className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-lg"
              title="Cancel"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </td>
      </tr>
    );
  }
  
  // Calculate effective rate with markup
  const effectiveRate = editData.baseRate * (1 + (editData.markupPct || 0) / 100);
  
  return (
    <tr className={`hover:bg-gray-50 ${!role.active ? 'opacity-50' : ''}`}>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-900">
            {ROLE_OPTIONS.find(r => r.key === role.role)?.label || role.role}
          </span>
          {!role.active && (
            <span className="px-1.5 py-0.5 text-[10px] font-medium bg-gray-100 text-gray-500 rounded">
              Inactive
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">
        {role.currency || currency}
      </td>
      <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">
        {currencySymbol}{role.baseRate?.toLocaleString()}
      </td>
      <td className="px-4 py-3 text-sm text-gray-500 text-right">
        {role.minRate != null ? `${currencySymbol}${role.minRate.toLocaleString()}` : '—'}
      </td>
      <td className="px-4 py-3 text-sm text-gray-500 text-right">
        {role.maxRate != null ? `${currencySymbol}${role.maxRate.toLocaleString()}` : '—'}
      </td>
      <td className="px-4 py-3 text-sm text-gray-600 text-right">
        {role.markupPct || 0}%
      </td>
      <td className="px-4 py-3 text-center">
        {role.active ? (
          <Check className="w-4 h-4 text-green-500 mx-auto" />
        ) : (
          <X className="w-4 h-4 text-gray-300 mx-auto" />
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1 justify-end">
          <button
            onClick={() => setEditing(role.id)}
            className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg"
            title="Edit"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(role.id)}
            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );
};

/**
 * Main Rate Card Page
 */
const RateCardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getFeature } = useProductMode();
  
  // TENANT ISOLATION: Use impersonated tenant if sysadmin is impersonating
  const tenantId = localStorage.getItem('impersonated_tenant_id') || user?.tenant?.id || user?.tenant_id;
  const userId = user?.id || user?._id;
  
  // Check features
  const isConsultingEnabled = getFeature('consulting.mode', false);
  const isRateCardEnabled = getFeature('consulting.ratecard', false);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rateCard, setRateCard] = useState([]);
  const [defaultCurrency, setDefaultCurrency] = useState('USD');
  const [editingId, setEditingId] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  
  // Load rate card
  const loadRateCard = useCallback(async () => {
    if (!tenantId) return;
    
    setLoading(true);
    try {
      const response = await api.getRateCard(tenantId);
      setRateCard(response.data.rateCard || []);
      setDefaultCurrency(response.data.defaultCurrency || 'USD');
      setLastSaved(response.data.updatedAt);
      setHasChanges(false);
    } catch (err) {
      console.error('Failed to load rate card:', err);
      if (err.response?.status !== 403) {
        toast.error('Failed to load rate card');
      }
    } finally {
      setLoading(false);
    }
  }, [tenantId]);
  
  useEffect(() => {
    if (isRateCardEnabled) {
      loadRateCard();
    } else {
      setLoading(false);
    }
  }, [loadRateCard, isRateCardEnabled]);
  
  // Add new role
  const addRole = () => {
    const usedRoles = rateCard.map(r => r.role);
    const availableRole = ROLE_OPTIONS.find(r => !usedRoles.includes(r.key))?.key || 'Other';
    
    const newRole = {
      id: `temp_${Date.now()}`,
      role: availableRole,
      currency: defaultCurrency,
      baseRate: 100,
      minRate: null,
      maxRate: null,
      markupPct: 0,
      active: true,
    };
    
    setRateCard([...rateCard, newRole]);
    setEditingId(newRole.id);
    setHasChanges(true);
  };
  
  // Update role
  const updateRole = (updatedRole) => {
    setRateCard(rateCard.map(r => r.id === updatedRole.id ? updatedRole : r));
    setHasChanges(true);
  };
  
  // Delete role
  const deleteRole = (roleId) => {
    if (!window.confirm('Are you sure you want to delete this role?')) return;
    setRateCard(rateCard.filter(r => r.id !== roleId));
    setHasChanges(true);
  };
  
  // Save rate card
  const saveRateCard = async () => {
    setSaving(true);
    try {
      const response = await api.saveRateCard(tenantId, {
        roles: rateCard,
        defaultCurrency,
      }, userId);
      
      setRateCard(response.data.rateCard || []);
      setLastSaved(response.data.updatedAt);
      setHasChanges(false);
      toast.success('Rate card saved successfully');
    } catch (err) {
      console.error('Failed to save rate card:', err);
      toast.error(err.response?.data?.detail || 'Failed to save rate card');
    } finally {
      setSaving(false);
    }
  };
  
  // Feature gate
  if (!isConsultingEnabled || !isRateCardEnabled) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-8">
        <TierGate
          requiredTier="business"
          feature="Rate Card Management"
          description="Configure role-based rates and currency for proposal estimation and pricing."
        />
      </div>
    );
  }
  
  // Calculate summary stats
  const activeRoles = rateCard.filter(r => r.active);
  const avgRate = activeRoles.length > 0 
    ? activeRoles.reduce((sum, r) => sum + r.baseRate, 0) / activeRoles.length 
    : 0;
  const avgMarkup = activeRoles.length > 0
    ? activeRoles.reduce((sum, r) => sum + (r.markupPct || 0), 0) / activeRoles.length
    : 0;
  
  const currencySymbol = CURRENCY_OPTIONS.find(c => c.code === defaultCurrency)?.symbol || '$';
  
  return (
    <div className="min-h-screen bg-gray-50" data-testid="rate-card-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-gray-900">Rate Card</h1>
                  <p className="text-xs text-gray-500">Manage role-based rates for proposals</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {lastSaved && (
                <span className="text-xs text-gray-500">
                  Last saved: {new Date(lastSaved).toLocaleString()}
                </span>
              )}
              <button
                onClick={loadRateCard}
                disabled={loading}
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                onClick={saveRateCard}
                disabled={saving || !hasChanges}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="save-rate-card-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-w-5xl mx-auto p-6 space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-xl border border-gray-200">
            <div className="text-xs text-gray-500 mb-1">Active Roles</div>
            <div className="text-2xl font-bold text-gray-900">{activeRoles.length}</div>
          </div>
          <div className="bg-white p-4 rounded-xl border border-gray-200">
            <div className="text-xs text-gray-500 mb-1">Avg. Base Rate</div>
            <div className="text-2xl font-bold text-gray-900">
              {currencySymbol}{avgRate.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </div>
          </div>
          <div className="bg-white p-4 rounded-xl border border-gray-200">
            <div className="text-xs text-gray-500 mb-1">Avg. Markup</div>
            <div className="text-2xl font-bold text-gray-900">
              {avgMarkup.toFixed(1)}%
            </div>
          </div>
          <div className="bg-white p-4 rounded-xl border border-gray-200">
            <div className="text-xs text-gray-500 mb-1">Default Currency</div>
            <select
              value={defaultCurrency}
              onChange={(e) => {
                setDefaultCurrency(e.target.value);
                setHasChanges(true);
              }}
              className="mt-1 w-full px-2 py-1 text-lg font-bold text-gray-900 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500"
              data-testid="default-currency-select"
            >
              {CURRENCY_OPTIONS.map(opt => (
                <option key={opt.code} value={opt.code}>
                  {opt.code} ({opt.symbol})
                </option>
              ))}
            </select>
          </div>
        </div>
        
        {/* Unsaved Changes Warning */}
        {hasChanges && (
          <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            <span className="text-sm text-amber-700">You have unsaved changes</span>
          </div>
        )}
        
        {/* Rate Card Table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <h2 className="font-medium text-gray-900">Role Rates</h2>
            <button
              onClick={addRole}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              data-testid="add-role-btn"
            >
              <Plus className="w-4 h-4" />
              Add Role
            </button>
          </div>
          
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
            </div>
          ) : rateCard.length === 0 ? (
            <div className="text-center py-12">
              <Settings className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No rates configured</h3>
              <p className="text-sm text-gray-500 mb-4">
                Add role rates to enable cost estimation in proposals
              </p>
              <button
                onClick={addRole}
                className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                <Plus className="w-4 h-4" />
                Add First Role
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Currency
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Base Rate
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Min
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Max
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Markup %
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Active
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rateCard.map(role => (
                    <RateCardRow
                      key={role.id}
                      role={role}
                      onUpdate={updateRole}
                      onDelete={deleteRole}
                      isEditing={editingId === role.id}
                      setEditing={setEditingId}
                      currency={defaultCurrency}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        
        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <h3 className="font-medium text-blue-900 mb-2">How Rate Cards Work</h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• <strong>Base Rate:</strong> The standard hourly rate for each role</li>
            <li>• <strong>Min/Max:</strong> Optional bounds for rate negotiation</li>
            <li>• <strong>Markup %:</strong> Applied on top of base rate for billing (effective rate = base × (1 + markup%))</li>
            <li>• <strong>Active:</strong> Only active roles are used in estimation and proposals</li>
            <li>• When all estimation roles have matching rates, costs appear in proposals; otherwise, hours-only mode is used</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default RateCardPage;
